import { useContext } from 'react'
import { DateTime } from 'luxon'
import type { AppDispatch } from '@redux/store'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import { launchModal } from '@domain-group/javascript-utils'

import type { UseFormOptions, UseFormPayload as UseFormBasePayload } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import { setTimeToDayEnd } from '@functions/dateTime'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as taskListActions from '@redux/modules/taskList'

import defaultFormState, { requiredFields } from '@models/taskList'

import type { TaskListModuleState, EntityOptions } from '@redux/modules/types'

import type { BrandModel } from '@models/brand'
import type { TaskListModel } from '@models/taskList'

export const calculateCompletionDecimal = (
  taskList: Partial<TaskListModel>,
  options?: { showRemaining: boolean },
) => {
  const { showRemaining } = options || {}

  // @ts-expect-error
  const totalTaskCount = taskList?.task_counts?.total ?? 0
  // @ts-expect-error
  const completedTaskCount = taskList?.task_counts?.completed ?? 0

  if (totalTaskCount){
    const completedDecimal = Math.round((completedTaskCount / totalTaskCount) * 100) / 100
    if (showRemaining) return 1 - completedDecimal
    return completedDecimal
  }

  return 0
}

export const generateUrls = (taskList: Partial<TaskListModel> = {}) => {
  const { id } = taskList

  return {
    taskListAnalyticsUrl: `#/projects/${id}/analytics`,
    editTaskListTasksUrl: `#/projects/${id}/tasks`,
    editTaskListUrl: `#/projects/${id}/edit`,
    taskListsIndexUrl: '#/projects',
  }
}

type EntityActionFunctionParams = {
  entityParams: Partial<TaskListModel>,
  dispatch: AppDispatch,
  requestOptions: EntityOptions,
}

const createTaskList = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { createTaskList: createFn } = taskListActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(createFn(updatedParams, requestOptions))
}

const createFromUsageReport = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { createFromUsageReport: createFromUsageReportFn } = taskListActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(createFromUsageReportFn(updatedParams, requestOptions))
}

const updateTaskList = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch, requestOptions } = params
  const { updateTaskList: updateFn } = taskListActions

  const updatedParams = {
    ...entityParams,
    due_date: setTimeToDayEnd({ dateString: entityParams.due_date }),
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

const toggleCompletion = (params: EntityActionFunctionParams) => {
  const { dispatch, entityParams, requestOptions } = params
  const { toggleTaskListCompletion: toggleCompletionFn } = taskListActions

  return dispatch(toggleCompletionFn(entityParams, requestOptions))
}

const deleteTaskList = (params: EntityActionFunctionParams) => {
  const { entityParams, dispatch } = params
  const { deleteTaskList: deleteFn } = taskListActions

  // @ts-expect-error TS2345
  return dispatch(deleteFn(entityParams))
}

type UseFormPayload = UseFormBasePayload & {
  creating?: boolean,
  updating?: boolean,
}

export function useTaskListForm(taskList: Partial<TaskListModel>, options?: UseFormOptions): UseFormPayload {
  const { validateOn } = options || {}

  const { creating, updating } = useSelector(reduxState => reduxState.taskLists)

  // Map entity so we can set start and end dates
  // @ts-expect-error
  const dueDateString = taskList?.dates?.due?.date_time_with_timezone

  const entity = {
    ...taskList,
    due_date:
      taskList.id && dueDateString
        ? DateTime.fromISO(dueDateString).toFormat('yyyy-MM-dd')
        : taskList.due_date,
  }

  const taskListForm = useForm(
    defaultFormState,
    { entity, requiredFields: [...requiredFields], validateOn },
    [taskList.id, taskList.cache_key],
  )

  return {
    ...taskListForm,
    creating,
    updating,
  }
}

export const useRelations = (entity: Partial<TaskListModel> = {}) => {
  const { brand_id, user_id } = entity

  const { brands, users } = useSelector(reduxState => reduxState.entities)

  const brand = brand_id && brands[brand_id] ? brands[brand_id] : {}
  const user = user_id && users[user_id] ? users[user_id] : {}

  return {
    brand,
    user,
  }
}

interface UseTaskListOptions {
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

export type UseTaskListPayload = {
  brand: Partial<BrandModel>,
  callbacks: {
    createOrEditTaskList: (customPayload?: any) => void,
    createTaskList: (entityParams: Partial<TaskListModel>, requestOptions?: EntityOptions) => void,
    createFromUsageReport: (entityParams: Partial<TaskListModel>, reqOptions?: EntityOptions) => void,
    deleteTaskList: (entityParams: Partial<TaskListModel>, requestOptions?: EntityOptions) => void,
    toggleCompletion: (requestOptions?: EntityOptions) => void,
    updateTaskList: (entityParams: Partial<TaskListModel>, requestOptions?: EntityOptions) => void,
    viewAndProcessChecklist: () => void,
  },
  creating: boolean,
  loading: boolean,
  taskList: Partial<TaskListModel>,
  updating: boolean,
  urls: ReturnType<typeof generateUrls>,
}

function useTaskList(
  initEntity: Partial<TaskListModel> = {},
  options: UseTaskListOptions = {},
): UseTaskListPayload {
  const { performHttpRequests, requestOptions } = options || {}

  const { entity: taskList } = useLatestEntity(initEntity, 'taskLists')
  const { id } = taskList

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { brand } = useRelations(taskList)

  const { creating, loading, updating } = useSelector(reduxState => reduxState.taskLists)

  useReduxAction(
    'taskLists',
    'loadTaskList',
    {
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, reqOptions: EntityOptions) => action(id, reqOptions),
      // @ts-expect-error
      shouldPerformFn: (entityReducer: TaskListModuleState) => {
        const { loadedIds } = entityReducer
        // @ts-expect-error TS2345
        return performHttpRequests && !loadedIds?.includes(id)
      },
    },
  )

  return {
    brand,
    callbacks: {
      createOrEditTaskList: customPayload => launchModal({
        callbacks,
        modalKey: 'CreateOrEditTaskListModal',
        payload: {
          taskList,
          ...customPayload,
        },
      }),
      // @ts-expect-error TS2322
      createTaskList: (entityParams: Partial<TaskListModel>, entityRequestOptions: EntityOptions) => (
        createTaskList({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      // @ts-expect-error TS2322
      createFromUsageReport: (entityParams: Partial<TaskListModel>, entityRequestOptions: EntityOptions) => (
        createFromUsageReport({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      // @ts-expect-error TS2322
      deleteTaskList: (entityParams: Partial<TaskListModel>, entityRequestOptions: EntityOptions) => (
        deleteTaskList({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      // @ts-expect-error TS2322
      toggleCompletion: (entityParams: Partial<TaskListModel>, entityRequestOptions: EntityOptions) => (
        toggleCompletion({ dispatch, entityParams, requestOptions: entityRequestOptions })
      ),
      // @ts-expect-error TS2322
      updateTaskList: (entityParams: Partial<TaskListModel>, entityRequestOptions: EntityOptions) => (
        updateTaskList({ entityParams, dispatch, requestOptions: entityRequestOptions })
      ),
      viewAndProcessChecklist: () => launchModal({
        callbacks,
        modalKey: 'ViewAndProcessChecklistModal',
        payload: {
          taskList,
        },
      }),
    },
    creating,
    loading,
    taskList,
    updating,
    urls: generateUrls(taskList),
  }
}

export default useTaskList
